import React, { useEffect, useState } from "react";
import { FileSpreadsheet, FileText, ListChecks, ChevronDown, ChevronRight, Loader2, ExternalLink } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const fmt = (v) => (Number(v) || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s) => (s ? s.split("-").reverse().join(".") : "");

export const MissingReceiptsDialog = ({ companyId, onSelectEntry }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    if (!open || !companyId) return;
    setLoading(true);
    apiClient.get(`/companies/${companyId}/bank/missing-receipts`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Übersicht konnte nicht geladen werden"))
      .finally(() => setLoading(false));
  }, [open, companyId]);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const r = await apiClient.get(`/companies/${companyId}/bank/missing-receipts/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fehlende-belege.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export fehlgeschlagen");
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="rounded-full text-xs" data-testid="missing-receipts-button">
        <ListChecks className="h-3.5 w-3.5 mr-1.5" /> Fehlende Belege
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="missing-receipts-dialog">
          <DialogHeader>
            <DialogTitle>Fehlende Belege nach Lieferant</DialogTitle>
            <DialogDescription>
              Offene Bank-Belastungen ohne zugeordneten Beleg oder Rechnung – zum Heraussuchen bei Lieferanten und Portalen.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate-700" data-testid="missing-receipts-summary">
              {data ? (<><b>{data.count}</b> Belastungen · <b>{data.merchants}</b> Lieferanten · Total <b>CHF {fmt(data.total_chf)}</b></>) : "…"}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => exportFile("xlsx")} disabled={!!exporting || !data} data-testid="missing-receipts-export-xlsx">
                {exporting === "xlsx" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />} Excel
              </Button>
              <Button size="sm" variant="outline" onClick={() => exportFile("pdf")} disabled={!!exporting || !data} data-testid="missing-receipts-export-pdf">
                {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />} PDF
              </Button>
            </div>
          </div>
          <div className="overflow-y-auto mt-2 border rounded-lg divide-y">
            {loading && <div className="p-6 text-center text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Lade…</div>}
            {!loading && data && data.groups.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500">Alle Belastungen haben einen Beleg.</div>
            )}
            {!loading && data?.groups.map((g, i) => {
              const key = `${g.merchant}-${i}`;
              const isOpen = !!expanded[key];
              return (
                <div key={key} data-testid={`missing-group-${i}`}>
                  <button type="button" onClick={() => setExpanded((e) => ({ ...e, [key]: !isOpen }))}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50">
                    {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    <span className="font-medium text-slate-900 flex-1 truncate">{g.merchant}</span>
                    {g.portal_url && (
                      <a href={g.portal_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline whitespace-nowrap"
                        title={`${g.portal_name}: Rechnungen im Kundenkonto öffnen`} data-testid={`missing-portal-link-${i}`}>
                        <ExternalLink className="h-3 w-3" /> Zum Portal
                      </a>
                    )}
                    <span className="text-xs text-slate-500">{fmtDate(g.first_date)} – {fmtDate(g.last_date)}</span>
                    <span className="text-xs rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 font-semibold">{g.count}× fehlt</span>
                    <span className="text-sm font-mono text-slate-800 w-28 text-right">CHF {fmt(g.total_chf)}</span>
                  </button>
                  {isOpen && (
                    <table className="w-full text-xs bg-slate-50/60">
                      <tbody>
                        {g.items.map((it) => (
                          <tr key={it.journal_entry_id} className="border-t border-slate-100 hover:bg-white cursor-pointer"
                            onClick={() => { onSelectEntry?.(it.journal_entry_id); setOpen(false); }}
                            data-testid={`missing-item-${it.journal_entry_id}`}>
                            <td className="px-3 py-1.5 w-24 text-slate-600">{fmtDate(it.date)}</td>
                            <td className="px-2 py-1.5 w-24 text-slate-500">{it.purchase_date ? `Kauf ${fmtDate(it.purchase_date)}` : ""}</td>
                            <td className="px-2 py-1.5 w-24 text-right font-mono">CHF {fmt(it.amount_chf)}</td>
                            <td className="px-2 py-1.5 w-24 text-slate-500">{it.orig_amount ? `${it.orig_currency} ${fmt(it.orig_amount)}` : ""}</td>
                            <td className="px-2 py-1.5 text-slate-500 truncate max-w-[380px]">{it.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
